import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Session } from '../../lib/api';
import { sessionKey, useSession } from '../../lib/session';
import { errorText } from '../../i18n';
import { Logo, Modal } from '@vatman/ui';
import './auth.css';

type Legal = NonNullable<Session['legal']>;

/** Links to the published documents: `{url}/terms`, `/privacy`, `/consent`. */
function Doc({ legal, doc, children }: { legal: Legal; doc: 'terms' | 'privacy' | 'consent'; children?: React.ReactNode }) {
  return (
    <a href={`${legal.url}/${doc}`} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

/**
 * Two separate boxes, as 152-FZ wants consent to processing apart from other documents:
 * the terms with the privacy policy, and the consent itself.
 */
export function LegalChecks({ legal, terms, pd, onChange }: { legal: Legal; terms: boolean; pd: boolean; onChange: (v: { terms: boolean; pd: boolean }) => void }) {
  return (
    <div className="legal-checks">
      <label className="legal-check">
        <input type="checkbox" checked={terms} onChange={(e) => onChange({ terms: e.target.checked, pd })} required />
        <span>
          <Trans i18nKey="legal.acceptTerms" components={{ terms: <Doc legal={legal} doc="terms" />, privacy: <Doc legal={legal} doc="privacy" /> }} />
        </span>
      </label>
      <label className="legal-check">
        <input type="checkbox" checked={pd} onChange={(e) => onChange({ terms, pd: e.target.checked })} required />
        <span>
          <Trans i18nKey="legal.acceptPd" components={{ consent: <Doc legal={legal} doc="consent" /> }} />
        </span>
      </label>
    </div>
  );
}

/** Clears what this browser kept for the account: query cache and offline copies of boards. */
export function forgetLocal(qc: ReturnType<typeof useQueryClient>) {
  qc.clear();
  indexedDB.databases?.().then((dbs) => dbs.forEach((d) => d.name?.startsWith('vatman:') && indexedDB.deleteDatabase(d.name)));
}

/**
 * Over everything while the signed-in person has not accepted the current documents: after a
 * magic link or Yandex ID / VK ID sign-in that made a new account, or when a new edition is out.
 */
export function ConsentGate() {
  const { t } = useTranslation();
  const session = useSession();
  const qc = useQueryClient();
  const [v, setV] = useState({ terms: false, pd: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const legal = session.data?.legal;
  const shown = !!(session.data?.user && session.data.consentRequired && legal);
  // The app underneath can be neither clicked nor reached with the keyboard.
  useEffect(() => {
    const root = document.getElementById('root');
    root?.toggleAttribute('inert', shown);
    return () => root?.removeAttribute('inert');
  }, [shown]);
  if (!shown || !legal) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/me/consent');
      await qc.invalidateQueries({ queryKey: sessionKey });
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'internal'));
    } finally {
      setBusy(false);
    }
  };
  const logout = async () => {
    await api.post('/auth/logout');
    forgetLocal(qc);
    location.assign('/login');
  };

  return createPortal(
    <div className="consent-gate" role="dialog" aria-modal="true" aria-labelledby="consent-title">
      <form className="auth-card" onSubmit={submit}>
        <Logo size={30} />
        <h2 id="consent-title">{t('legal.gateTitle')}</h2>
        <p className="muted" style={{ margin: 0 }}>
          {t('legal.gateText')}
        </p>
        <LegalChecks legal={legal} terms={v.terms} pd={v.pd} onChange={setV} />
        {error && <div className="error-text">{error}</div>}
        <button className="btn primary lg block" disabled={busy || !v.terms || !v.pd}>
          {t('legal.accept')}
        </button>
        <button type="button" className="btn lg block" onClick={() => void logout()}>
          {t('auth.logout')}
        </button>
      </form>
    </div>,
    document.body,
  );
}

/** Deleting the account: the person types their email to confirm. */
export function DeleteAccountDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const session = useSession();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const email = session.data?.user?.email ?? '';
  const legal = session.data?.legal;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.del('/me', { confirm });
      forgetLocal(qc);
      location.assign('/login');
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'internal'));
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={t('legal.deleteTitle')} width={480}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 14, marginTop: 12 }}>
        <ul className="legal-list">
          <li>{t('legal.deleteGone')}</li>
          <li>{t('legal.deleteStay')}</li>
          <li>{t('legal.deleteFinal')}</li>
        </ul>
        {legal && (
          <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-3)' }}>
            <Trans i18nKey="legal.deletePolicy" components={{ privacy: <Doc legal={legal} doc="privacy" /> }} />
          </p>
        )}
        <label className="field">
          <span>{t('legal.deleteConfirm', { email })}</span>
          <input className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={email} autoComplete="off" autoFocus />
        </label>
        {error && <div className="error-text">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn danger solid" disabled={busy || confirm.trim().toLowerCase() !== email.toLowerCase()}>
            {t('legal.deleteButton')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
